import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from 'react-bootstrap';
import { BiGridVertical, BiTrash, BiEdit, BiCopy } from 'react-icons/bi';
import { TemplateField, FIELD_TYPE_LABELS, CHOICE_TYPES } from '../../types/template';

// ── Single sortable field card ─────────────────────────────────────────────

function FieldCard({
    field,
    isSelected,
    onSelect,
    onDuplicate,
    onDelete,
}: {
    field: TemplateField;
    isSelected: boolean;
    onSelect: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
}) {
    const {
        attributes, listeners, setNodeRef,
        transform, transition, isDragging,
    } = useSortable({ id: field.id });

    const style: React.CSSProperties = {
        transform:  CSS.Transform.toString(transform),
        transition,
        opacity:    isDragging ? 0.4 : 1,
    };

    const previewText = (): React.ReactNode => {
        if (CHOICE_TYPES.includes(field.type)) {
            const opts = field.options ?? [];
            if (opts.length === 0) return <span className="canvas-field-card__preview">No options yet</span>;
            return (
                <div className="canvas-field-card__preview">
                    {opts.slice(0, 3).map(o => <div key={o.id}>• {o.label}</div>)}
                    {opts.length > 3 && <div>+{opts.length - 3} more…</div>}
                </div>
            );
        }
        if (field.type === 'star_rating')
            return <div className="canvas-field-card__preview">{'★'.repeat(field.maxStars ?? 5)}</div>;
        if (field.type === 'textbox' && field.inputType)
            return <div className="canvas-field-card__preview">Input type: {field.inputType}</div>;
        if (field.placeholder)
            return <div className="canvas-field-card__preview">{field.placeholder}</div>;
        return null;
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`canvas-field-card${isSelected ? ' canvas-field-card--selected' : ''}`}
            onClick={e => { e.stopPropagation(); onSelect(); }}
        >
            <div className="canvas-field-card__header">
                <span className="canvas-field-card__drag-handle" {...attributes} {...listeners}>
                    <BiGridVertical size={16} />
                </span>
                <span>{FIELD_TYPE_LABELS[field.type]}</span>
                <div className="canvas-field-card__actions">
                    <Button
                        size="sm" variant="link" className="p-0 text-secondary"
                        onClick={e => { e.stopPropagation(); onSelect(); }}
                        title="Edit"
                    >
                        <BiEdit size={15} />
                    </Button>
                    <Button
                        size="sm" variant="link" className="p-0 text-secondary ms-1"
                        onClick={e => { e.stopPropagation(); onDuplicate(); }}
                        title="Duplicate"
                    >
                        <BiCopy size={15} />
                    </Button>
                    <Button
                        size="sm" variant="link" className="p-0 text-danger ms-1"
                        onClick={e => { e.stopPropagation(); onDelete(); }}
                        title="Delete"
                    >
                        <BiTrash size={15} />
                    </Button>
                </div>
            </div>
            <div className="canvas-field-card__body">
                <div className="canvas-field-card__label">
                    {field.label || <span className="text-muted fst-italic">Untitled question</span>}
                    {field.required && <span className="canvas-field-card__required-badge">*</span>}
                </div>
                {previewText()}
            </div>
        </div>
    );
}

// ── Canvas ─────────────────────────────────────────────────────────────────

interface Props {
    fields: TemplateField[];
    selectedFieldId: string | null;
    onSelect: (id: string) => void;
    onDuplicate: (id: string) => void;
    onDelete: (id: string) => void;
}

export default function BuilderCanvas({ fields, selectedFieldId, onSelect, onDuplicate, onDelete }: Props) {
    const { setNodeRef, isOver } = useDroppable({ id: 'canvas' });

    return (
        <div
            ref={setNodeRef}
            className={`builder-canvas${isOver ? ' builder-canvas--over' : ''}`}
            onClick={() => onSelect('')}
        >
            {fields.length === 0 ? (
                <div className={`builder-canvas__empty${isOver ? ' builder-canvas__empty--over' : ''}`}>
                    <span style={{ fontSize: 36 }}>📋</span>
                    <span>Drag components here to build your form</span>
                    <small className="text-muted">Or click a component in the left panel</small>
                </div>
            ) : (
                <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    {fields.map(field => (
                        <FieldCard
                            key={field.id}
                            field={field}
                            isSelected={field.id === selectedFieldId}
                            onSelect={() => onSelect(field.id)}
                            onDuplicate={() => onDuplicate(field.id)}
                            onDelete={() => onDelete(field.id)}
                        />
                    ))}
                </SortableContext>
            )}

            {/* Drop zone at the bottom when there are fields */}
            {fields.length > 0 && isOver && (
                <div className="builder-canvas__drop-zone builder-canvas__drop-zone--over" style={{ height: 56 }} />
            )}
        </div>
    );
}
